import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ToolCard } from '@/components/common/ToolCard';
import { AdPlaceholder } from '@/components/common/AdPlaceholder';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search } from 'lucide-react';
import { useState } from 'react';
import {
  Calculator,
  TrendingUp,
  CreditCard,
  Receipt,
  Briefcase,
  PiggyBank,
  Target,
  AlertCircle,
  Wallet,
  DollarSign,
  Percent,
  FileText,
  BarChart3,
  LineChart,
  PieChart,
  TrendingDown,
} from 'lucide-react';

const ExploreToolsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const allTools = [
    // Personal Finance
    {
      name: 'Budget Calculator',
      description: 'Plan and track your monthly income and expenses with detailed breakdowns.',
      category: 'Personal Finance',
      icon: Calculator,
      href: '/tools/budget-calculator',
      tags: ['Budget', 'Planning', 'Monthly'],
      popular: true,
    },
    {
      name: 'Expense Tracker',
      description: 'Monitor your daily expenses and identify spending patterns over time.',
      category: 'Personal Finance',
      icon: Wallet,
      href: '/tools/expense-tracker',
      tags: ['Expenses', 'Tracking', 'Daily'],
    },
    {
      name: 'Savings Goal Calculator',
      description: 'Set and achieve your savings goals with a clear monthly contribution plan.',
      category: 'Personal Finance',
      icon: Target,
      href: '/tools/savings-goal-calculator',
      tags: ['Savings', 'Goals', 'Planning'],
    },
    {
      name: 'Emergency Fund Calculator',
      description: 'Calculate how much you need to save for unexpected financial emergencies.',
      category: 'Personal Finance',
      icon: AlertCircle,
      href: '/tools/emergency-fund-calculator',
      tags: ['Emergency', 'Savings', 'Security'],
    },
    {
      name: 'Net Worth Calculator',
      description: 'Calculate your total net worth by tracking assets and liabilities.',
      category: 'Personal Finance',
      icon: PiggyBank,
      href: '/tools/net-worth-calculator',
      tags: ['Net Worth', 'Assets', 'Wealth'],
    },
    // Investment Tools
    {
      name: 'SIP Calculator',
      description: 'Calculate potential returns from systematic investment plans in mutual funds.',
      category: 'Investment',
      icon: TrendingUp,
      href: '/tools/sip-calculator',
      tags: ['SIP', 'Mutual Funds', 'Investment'],
      popular: true,
    },
    {
      name: 'Lumpsum Calculator',
      description: 'Estimate returns on one-time lumpsum investments in mutual funds.',
      category: 'Investment',
      icon: DollarSign,
      href: '/tools/lumpsum-calculator',
      tags: ['Lumpsum', 'Investment', 'Returns'],
    },
    {
      name: 'Compound Interest Calculator',
      description: 'See how your money grows with the power of compound interest over time.',
      category: 'Investment',
      icon: TrendingUp,
      href: '/tools/compound-interest-calculator',
      tags: ['Compound', 'Interest', 'Growth'],
      popular: true,
    },
    {
      name: 'Stock Return Calculator',
      description: 'Calculate historical and projected returns on stock investments.',
      category: 'Investment',
      icon: LineChart,
      href: '/tools/stock-return-calculator',
      tags: ['Stocks', 'Returns', 'Investment'],
    },
    {
      name: 'Dividend Calculator',
      description: 'Calculate dividend income from your stock portfolio investments.',
      category: 'Investment',
      icon: PieChart,
      href: '/tools/dividend-calculator',
      tags: ['Dividend', 'Income', 'Stocks'],
    },
    {
      name: 'Mutual Fund Calculator',
      description: 'Analyze and compare mutual fund returns for informed investment decisions.',
      category: 'Investment',
      icon: BarChart3,
      href: '/tools/mutual-fund-calculator',
      tags: ['Mutual Funds', 'Analysis', 'Returns'],
    },
    // Loan & Credit
    {
      name: 'EMI Calculator',
      description: 'Calculate your equated monthly installments for any type of loan.',
      category: 'Loan & Credit',
      icon: CreditCard,
      href: '/tools/emi-calculator',
      tags: ['EMI', 'Loan', 'Installment'],
      popular: true,
    },
    {
      name: 'Home Loan Calculator',
      description: 'Calculate EMI, interest, and repayment schedule for home loans.',
      category: 'Loan & Credit',
      icon: CreditCard,
      href: '/tools/home-loan-calculator',
      tags: ['Home Loan', 'Mortgage', 'EMI'],
    },
    {
      name: 'Car Loan Calculator',
      description: 'Determine monthly payments and total interest for car loans.',
      category: 'Loan & Credit',
      icon: CreditCard,
      href: '/tools/car-loan-calculator',
      tags: ['Car Loan', 'Auto', 'EMI'],
    },
    {
      name: 'Personal Loan Calculator',
      description: 'Calculate repayment terms and interest for personal loans.',
      category: 'Loan & Credit',
      icon: Wallet,
      href: '/tools/personal-loan-calculator',
      tags: ['Personal Loan', 'EMI', 'Interest'],
    },
    {
      name: 'Credit Card Calculator',
      description: 'Calculate interest charges and payoff time for credit card debt.',
      category: 'Loan & Credit',
      icon: CreditCard,
      href: '/tools/credit-card-calculator',
      tags: ['Credit Card', 'Debt', 'Interest'],
    },
    {
      name: 'Loan Eligibility Calculator',
      description: 'Check your loan eligibility based on income and existing obligations.',
      category: 'Loan & Credit',
      icon: FileText,
      href: '/tools/loan-eligibility-calculator',
      tags: ['Eligibility', 'Loan', 'Assessment'],
    },
    // Tax & Salary
    {
      name: 'Income Tax Calculator',
      description: 'Calculate your income tax liability and plan tax-saving investments.',
      category: 'Tax & Salary',
      icon: Receipt,
      href: '/tools/income-tax-calculator',
      tags: ['Tax', 'Income', 'Planning'],
      popular: true,
    },
    {
      name: 'Salary Breakdown Calculator',
      description: 'Get a detailed breakdown of your salary components and deductions.',
      category: 'Tax & Salary',
      icon: FileText,
      href: '/tools/salary-breakdown-calculator',
      tags: ['Salary', 'Breakdown', 'Components'],
    },
    {
      name: 'Take Home Salary Calculator',
      description: 'Calculate your net take-home salary after all deductions.',
      category: 'Tax & Salary',
      icon: DollarSign,
      href: '/tools/take-home-salary-calculator',
      tags: ['Salary', 'Take Home', 'Net Pay'],
    },
    {
      name: 'GST Calculator',
      description: 'Calculate GST amount and total price with inclusive/exclusive options.',
      category: 'Tax & Salary',
      icon: Percent,
      href: '/tools/gst-calculator',
      tags: ['GST', 'Tax', 'Business'],
    },
    {
      name: 'TDS Calculator',
      description: 'Calculate tax deducted at source for various income categories.',
      category: 'Tax & Salary',
      icon: Receipt,
      href: '/tools/tds-calculator',
      tags: ['TDS', 'Tax', 'Deduction'],
    },
    // Business Tools
    {
      name: 'Profit Margin Calculator',
      description: 'Calculate gross, net, and operating profit margins for your business.',
      category: 'Business',
      icon: Briefcase,
      href: '/tools/profit-margin-calculator',
      tags: ['Profit', 'Margin', 'Business'],
    },
    {
      name: 'Break Even Calculator',
      description: 'Determine the break-even point for your business or product.',
      category: 'Business',
      icon: TrendingDown,
      href: '/tools/break-even-calculator',
      tags: ['Break Even', 'Business', 'Analysis'],
    },
    {
      name: 'ROI Calculator',
      description: 'Calculate return on investment for business projects and decisions.',
      category: 'Business',
      icon: BarChart3,
      href: '/tools/roi-calculator',
      tags: ['ROI', 'Return', 'Investment'],
      popular: true,
    },
    {
      name: 'Business Valuation Calculator',
      description: 'Estimate the value of your business using multiple valuation methods.',
      category: 'Business',
      icon: Briefcase,
      href: '/tools/business-valuation-calculator',
      tags: ['Valuation', 'Business', 'Worth'],
    },
    {
      name: 'Cash Flow Calculator',
      description: 'Track and forecast your business cash inflows and outflows.',
      category: 'Business',
      icon: DollarSign,
      href: '/tools/cash-flow-calculator',
      tags: ['Cash Flow', 'Business', 'Finance'],
    },
  ];

  const categories = [
    { id: 'all', name: 'All Tools', count: allTools.length },
    {
      id: 'Personal Finance',
      name: 'Personal Finance',
      count: allTools.filter((t) => t.category === 'Personal Finance').length,
    },
    {
      id: 'Investment',
      name: 'Investment',
      count: allTools.filter((t) => t.category === 'Investment').length,
    },
    {
      id: 'Loan & Credit',
      name: 'Loan & Credit',
      count: allTools.filter((t) => t.category === 'Loan & Credit').length,
    },
    {
      id: 'Tax & Salary',
      name: 'Tax & Salary',
      count: allTools.filter((t) => t.category === 'Tax & Salary').length,
    },
    {
      id: 'Business',
      name: 'Business',
      count: allTools.filter((t) => t.category === 'Business').length,
    },
  ];

  const filteredTools = allTools.filter((tool) => {
    const matchesCategory = activeCategory === 'all' || tool.category === activeCategory;
    const matchesSearch =
      searchQuery === '' ||
      tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="gradient-hero py-12 md:py-16 border-b border-border">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
              27+ Tools Available
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-4">
              Explore All <span className="text-gradient-primary">Financial Tools</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Browse our complete collection of calculators and tools designed to simplify your financial planning
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <Input
                type="text"
                placeholder="Search for calculators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-6 bg-background">
        <div className="container mx-auto px-4">
          <AdPlaceholder size="leaderboard" position="inline" />
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Category Tabs */}
              <Tabs value={activeCategory} onValueChange={setActiveCategory} className="mb-8">
                <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto gap-2 bg-muted/50">
                  {categories.map((category) => (
                    <TabsTrigger
                      key={category.id}
                      value={category.id}
                      className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                    >
                      {category.name} ({category.count})
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>

              {/* Results Count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-muted-foreground">
                  Showing <span className="font-semibold text-foreground">{filteredTools.length}</span> tools
                </p>
              </div>

              {/* Tools Grid */}
              {filteredTools.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTools.map((tool, index) => (
                    <ToolCard key={index} tool={tool} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                      <Search className="w-8 h-8 text-muted-foreground" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">No tools found</h3>
                  <p className="text-muted-foreground">Try adjusting your search or category filter</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <AdPlaceholder size="medium" position="sidebar" />
              <AdPlaceholder size="medium" position="sidebar" />
              <AdPlaceholder size="square" position="sidebar" />
            </div>
          </div>
        </div>
      </section>

      {/* Ad Placement */}
      <section className="py-6 bg-muted/30">
        <div className="container mx-auto px-4">
          <AdPlaceholder size="banner" position="inline" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExploreToolsPage;
